export default defineAppConfig({
  ui: {
    colors: {
      primary: 'primary',
      neutral: 'slate',
      fuchsia: 'fuchsia'
    },

    // Button - clean, no glow
    button: {
      slots: {
        base: 'rounded-lg font-medium cursor-pointer'
      }
    },

    // PageHero - restrained sizes, alignment left to orientation variants
    pageHero: {
      slots: {
        container: 'py-24 sm:py-28 lg:py-32 gap-12 sm:gap-16',
        title: 'text-4xl sm:text-5xl lg:text-6xl',
        description: 'text-base sm:text-lg'
      }
    },

    // PageSection - restrained sizes, alignment left to orientation variants
    pageSection: {
      slots: {
        container: 'py-16 sm:py-20 lg:py-24 gap-8 sm:gap-12',
        title: 'text-2xl sm:text-3xl lg:text-4xl',
        description: 'text-base sm:text-lg'
      }
    },

    // PageCard - defined surface, quiet hover. The lift shadow applies only to
    // filled variants; naked/ghost header cards stay flat.
    pageCard: {
      slots: {
        root: 'rounded-xl',
        title: 'text-base font-semibold',
        description: 'text-sm text-muted leading-relaxed'
      },
      variants: {
        variant: {
          outline: { root: 'shadow-(--surface-shadow)' },
          soft: { root: 'shadow-(--surface-shadow)' },
          subtle: { root: 'shadow-(--surface-shadow)' }
        }
      }
    },

    // PageCTA - calm bottom call-to-action
    pageCTA: {
      slots: {
        title: 'text-2xl sm:text-3xl',
        description: 'text-base sm:text-lg'
      }
    },

    // Footer - subtle top border
    footer: {
      slots: {
        root: 'border-t border-muted'
      }
    },

    // NavigationMenu - quiet pills with a primary active state
    navigationMenu: {
      slots: {
        link: 'transition-all duration-200 rounded-xl text-base py-2.5 px-3.5 before:rounded-xl',
        linkLeadingIcon: 'transition-colors duration-200 size-5',
        linkLabel: 'font-semibold'
      },
      compoundVariants: [
        {
          variant: 'pill' as const,
          active: true,
          highlight: false,
          class: {
            link: 'before:bg-primary-500/10 text-highlighted',
            linkLeadingIcon: 'text-primary-400'
          }
        },
        {
          disabled: false,
          active: false,
          variant: 'pill' as const,
          class: {
            link: 'hover:before:bg-elevated/60 text-muted hover:text-highlighted',
            linkLeadingIcon: 'text-dimmed group-hover:text-muted'
          }
        }
      ]
    },

    // Separator - very subtle
    separator: {
      slots: {
        root: 'border-muted'
      }
    },

    // Input - rounded-xl with subtle border
    input: {
      slots: {
        root: 'transition-colors',
        base: 'rounded-xl bg-default border border-accented ring-0 focus:ring-0 focus-visible:ring-0 focus:border-primary-400/60 focus-visible:border-primary-400/60 font-medium placeholder:text-dimmed'
      },
      variants: {
        variant: {
          outline: 'bg-default border border-accented ring-0 focus:ring-0 focus-visible:ring-0 focus:border-primary-400/60 focus-visible:border-primary-400/60',
          soft: 'bg-elevated/50 border border-accented ring-0',
          subtle: 'bg-elevated border border-accented ring-0 focus:ring-0 focus-visible:ring-0 focus:border-primary-400/60 focus-visible:border-primary-400/60',
          ghost: 'bg-transparent border border-transparent ring-0',
          none: 'bg-transparent border-0 ring-0'
        }
      }
    },

    // Textarea - match input styling
    textarea: {
      slots: {
        root: 'transition-colors',
        base: 'rounded-xl bg-default border border-accented ring-0 focus:ring-0 focus-visible:ring-0 focus:border-primary-400/60 focus-visible:border-primary-400/60 font-medium placeholder:text-dimmed'
      }
    },

    // Accordion - subtle borders
    accordion: {
      slots: {
        header: 'rounded-lg hover:bg-elevated/50 transition-colors duration-200',
        item: 'border-b border-default last:border-b-0',
        body: 'px-1',
        leadingIcon: 'text-primary-400 size-4'
      }
    },

    // Slideover - subtle panel borders
    slideover: {
      slots: {
        header: 'border-b border-default px-4 py-3',
        body: 'px-4 py-3',
        footer: 'border-t border-default px-4 py-3'
      }
    },

    // Select - match input styling
    select: {
      slots: {
        base: 'rounded-xl bg-default border border-accented ring-0 focus:ring-0 focus-visible:ring-0 focus:border-primary-400/60 cursor-pointer',
        content: 'min-w-fit'
      }
    },

    // Dashboard Sidebar - floating panel
    dashboardSidebar: {
      slots: {
        root: 'border-e-0 m-3 rounded-2xl border border-default shadow-(--surface-shadow) !min-h-0 h-[calc(100svh-1.5rem)]',
        header: 'shrink-0 flex items-center gap-2 px-5 py-4',
        body: 'flex flex-col gap-3 flex-1 overflow-y-auto px-3 py-3',
        footer: 'shrink-0 flex items-center gap-2 px-3 py-3 border-t border-default mx-2'
      }
    },

    // Dashboard Panel - floating glass content area
    dashboardPanel: {
      slots: {
        root: 'relative flex flex-col min-w-0 shrink-0 my-3 mr-3 rounded-2xl border border-default shadow-(--surface-shadow) overflow-hidden panel-glass !min-h-0 h-[calc(100svh-1.5rem)]',
        body: 'flex flex-col gap-6 sm:gap-8 flex-1 overflow-y-auto p-6 sm:p-8'
      }
    },

    // Dashboard Navbar - glass with branded title
    dashboardNavbar: {
      slots: {
        root: 'h-16 shrink-0 flex items-center justify-between border-b border-default px-6 sm:px-8 gap-2',
        title: 'flex items-center gap-2.5 text-lg font-semibold text-highlighted tracking-tight'
      }
    },

    // Dashboard Toolbar - matching glass style
    dashboardToolbar: {
      slots: {
        root: 'shrink-0 flex items-center justify-between border-b border-default px-6 sm:px-8 gap-2 overflow-x-auto min-h-[52px]'
      }
    },

    // PricingTable - green checks, red x icons
    pricingTable: {
      slots: {
        tierFeatureIcon: 'size-5 shrink-0 text-red-500'
      },
      variants: {
        active: {
          true: {
            tierFeatureIcon: 'text-green-500'
          }
        }
      }
    },

    // Carousel - game-style arrows
    carousel: {
      slots: {
        prev: 'absolute rounded-full border-0 bg-primary-500/25 hover:bg-primary-500/50 tint-primary-icon backdrop-blur-sm transition-all duration-200',
        next: 'absolute rounded-full border-0 bg-primary-500/25 hover:bg-primary-500/50 tint-primary-icon backdrop-blur-sm transition-all duration-200'
      }
    }
  }
})
